import { RasterImage } from '../types';
import { fitWithin } from './background';

/** Loads a File into an HTMLImageElement, resolving once its pixels are ready. */
function loadImageElement(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not decode the selected image'));
    };
    image.src = url;
  });
}

/**
 * Re-encodes an image file as a WebP data URL, downscaled so its longest edge is
 * at most `maxEdge` pixels. Keeps the embedded background small enough to live in
 * the project document without blowing the localStorage quota.
 */
export async function downscaleToWebp(
  file: File,
  maxEdge: number,
  quality: number
): Promise<RasterImage> {
  const image = await loadImageElement(file);
  const { width, height } = fitWithin(image.naturalWidth, image.naturalHeight, maxEdge);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Could not get a 2D canvas context to process the image');
  }
  context.drawImage(image, 0, 0, width, height);

  return { src: canvas.toDataURL('image/webp', quality), width, height };
}
